import React, { useEffect, useMemo, useState } from 'react';
import { HOSTED_IMAGES } from '../utils/imageHosting';

// raw → CDN
const RAW_PREFIX = 'https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main';
const CDN_PREFIX = 'https://cdn.jsdelivr.net/gh/richardsonstrategygroup/rsg-assets@main';
const toCdn = (u) => (u ? u.replace(RAW_PREFIX, CDN_PREFIX) : u);

// normalize "Philadelphia", "PHILADELPHIA, PA", "São Paulo" → "philadelphia" / "sao-paulo"
const normalizeCity = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // strip diacritics
    .replace(/[^a-z0-9]+/g, '-')                      // spaces & punctuation → hyphen
    .replace(/-+/g, '-')                              // collapse hyphens
    .replace(/^-|-$/g, '');                           // trim hyphens

// collapse hyphens for keys like "newyork", "losangeles"
const dehyphen = (s) => (s ? s.replace(/-/g, '') : s);

const HEROES = HOSTED_IMAGES.heroes || {};

export default function DynamicHeroImage({ className = '', fallbackImage, alt = 'Hero image' }) {
  const [cityKey, setCityKey] = useState(null);   // hyphenated key (e.g., "new-york")
  const [src, setSrc] = useState(fallbackImage);
  const [triedCdn, setTriedCdn] = useState(false);

  // debug override: ?city=philadelphia
  const debugCity = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('city');
  }, []);

  useEffect(() => {
    async function resolveHero() {
      try {
        const override = debugCity && normalizeCity(debugCity);
        if (override) {
          setCityKey(override);
        } else {
          // use prod proxy so it works on live
          const res = await fetch('/api/geo-hero', { credentials: 'omit' });
          const data = await res.json();
          const key = normalizeCity(data.city);     // e.g., "new-york"
          setCityKey(key || null);
          if (import.meta.env.DEV) console.log('Geo city:', data.city, '→ key:', key);
        }
      } catch (e) {
        if (import.meta.env.DEV) console.warn('Geo fetch failed, using default hero', e);
        setCityKey(null);
      }
    }
    resolveHero();
  }, [debugCity]);

  useEffect(() => {
    const flatKey = dehyphen(cityKey);                // e.g., "newyork"
    const byCity =
      (cityKey && HEROES[cityKey]) ||
      (flatKey && HEROES[flatKey]);                   // try both styles

    const chosen = byCity || HEROES.default || fallbackImage;
    setSrc(chosen);
    setTriedCdn(false);
  }, [cityKey, fallbackImage]);

  return (
    <img
      src={src}
      alt={alt}
      className={`rounded-2xl shadow-xl w-full h-64 md:h-96 object-cover ${className}`}
      loading="eager"
      onError={(e) => {
        if (!triedCdn) {
          setTriedCdn(true);
          const cdn = toCdn(src);
          e.currentTarget.src = cdn;
          if (import.meta.env.DEV) console.warn('Hero raw failed, retrying CDN:', cdn);
        } else if (src !== fallbackImage) {
          e.currentTarget.src = fallbackImage;
          if (import.meta.env.DEV) console.error('Hero still failing, falling back to default:', fallbackImage);
        }
      }}
    />
  );
}